import { createAsyncThunk } from "@reduxjs/toolkit"

import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import i18n from "../../../i18n.ts"
import { type RootState } from "../../../store.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import * as api from "../api/services.ts"
import {
  forgetAttachments,
  recallAttachments,
  rememberAttachments
} from "../helpers/pending-attachments.ts"
import type { Message, NotificationLevel } from "../types/messaging.types.ts"
import {
  type MessagingState,
  mergeMessages,
  messageArrived,
  messageChanged,
  messageDiscarded,
  messageFailed,
  messageQueued,
  messageRetrying,
  prependMessages,
  setConversations,
  setConversationsError,
  setConversationsLoading,
  setDraft,
  setMessages,
  setMessagesError,
  setMessagesLoading,
  setNotificationLevelFor,
  setOlderLoading,
  setUnreadCount,
  setUnreadTotal,
  upsertConversation
} from "./messaging.slice.ts"

interface Scope {
  portal?: boolean
}

export const fetchConversations = createAsyncThunk(
  "messaging/fetchConversations",
  async (
    params: {
      kind?: string
      project?: string
      unread?: string
      q?: string
    } & Scope,
    { dispatch, getState }
  ) => {
    const { portal, ...query } = params
    dispatch(setConversationsLoading(true))
    // A customer's own portal has no branch workspace to scope by.
    const activeBranchId = portal
      ? null
      : selectActiveBranchId(getState() as RootState)
    const response = await api.getConversations(
      { ...query, ...(activeBranchId ? { team: activeBranchId } : {}) },
      portal
    )
    if (!response.ok || !response.data) {
      dispatch(setConversationsError(true))
      return
    }
    dispatch(setConversations(response.data))
  }
)

export const fetchMessages = createAsyncThunk(
  "messaging/fetchMessages",
  async (params: { conversationId: string } & Scope, { dispatch }) => {
    dispatch(
      setMessagesLoading({
        conversationId: params.conversationId,
        loading: true
      })
    )
    const response = await api.getMessages(
      params.conversationId,
      {},
      params.portal
    )
    if (!response.ok || !response.data) {
      dispatch(
        setMessagesError({ conversationId: params.conversationId, error: true })
      )
      return
    }
    dispatch(
      setMessages({
        conversationId: params.conversationId,
        messages: response.data.results,
        hasMore: response.data.has_more
      })
    )
  }
)

export const fetchOlderMessages = createAsyncThunk(
  "messaging/fetchOlderMessages",
  async (
    params: {
      conversationId: string
      before: string
      beforeId?: string
    } & Scope,
    { dispatch }
  ) => {
    const { conversationId } = params
    dispatch(setOlderLoading({ conversationId, loading: true }))
    const response = await api.getMessages(
      conversationId,
      params.beforeId
        ? { before: params.before, before_id: params.beforeId }
        : { before: params.before },
      params.portal
    )
    dispatch(setOlderLoading({ conversationId, loading: false }))
    if (!response.ok || !response.data) return
    dispatch(
      prependMessages({
        conversationId,
        messages: response.data.results,
        hasMore: response.data.has_more
      })
    )
  },
  {
    condition: (params, { getState }) =>
      !(getState() as { messaging: MessagingState }).messaging.olderLoading[
        params.conversationId
      ]
  }
)

// A catch-up page is capped server-side; this bounds how far one reconnect
// will page before settling for what it has.
const CATCH_UP_PAGES = 20

/**
 * Every message after `after`, following `has_more` page by page, plus — when
 * asked — the older ones edited or deleted since. Null if the first page
 * fails; a later failure keeps what already arrived.
 */
const fetchEverythingAfter = async (
  conversationId: string,
  after: string,
  { portal, includeChanges }: Scope & { includeChanges: boolean }
): Promise<{ messages: Message[]; changed: Message[] } | null> => {
  const messages: Message[] = []
  const changed: Message[] = []
  let cursor = after
  for (let page = 0; page < CATCH_UP_PAGES; page += 1) {
    const response = await api.getMessages(
      conversationId,
      includeChanges && page === 0
        ? { after: cursor, include_changes: true }
        : { after: cursor },
      portal
    )
    if (!response.ok || !response.data)
      return page === 0 ? null : { messages, changed }
    messages.push(...response.data.results)
    changed.push(...(response.data.changed ?? []))
    const last = response.data.results[response.data.results.length - 1]
    if (!response.data.has_more || !last) break
    cursor = last.created
  }
  return { messages, changed }
}

/**
 * Everything the socket could not deliver while it was down.
 * `after` is the last message this client actually holds, so the catch-up is
 * exactly the gap — never a whole page to reconcile — and edits or deletions
 * made meanwhile to messages already held are applied too.
 */
export const catchUpMessages = createAsyncThunk(
  "messaging/catchUp",
  async (
    params: { conversationId: string; after: string } & Scope,
    { dispatch }
  ) => {
    const missed = await fetchEverythingAfter(
      params.conversationId,
      params.after,
      {
        portal: params.portal,
        includeChanges: true
      }
    )
    if (!missed) return
    missed.messages.forEach((message) => dispatch(messageArrived(message)))
    missed.changed.forEach((message) => dispatch(messageChanged(message)))
  }
)

/**
 * Bring a message older than the loaded page into the history.
 *
 * The server returns it with a few messages either side; everything after
 * that window is fetched too, so the history stays one unbroken run from the
 * target to the newest message instead of ending at the window.
 */
export const jumpToMessage = createAsyncThunk(
  "messaging/jumpToMessage",
  async (
    params: { conversationId: string; messageId: string } & Scope,
    { dispatch }
  ) => {
    const context = await api.getMessageContext(params.messageId, params.portal)
    if (!context.ok || !context.data) return
    const around = context.data.results
    const last = around[around.length - 1]
    let newer: Message[] = []
    if (last) {
      const after = await fetchEverythingAfter(
        params.conversationId,
        last.created,
        {
          portal: params.portal,
          includeChanges: false
        }
      )
      if (!after) return
      newer = after.messages
    }
    dispatch(
      mergeMessages({
        conversationId: params.conversationId,
        messages: [...around, ...newer]
      })
    )
  }
)

export const sendMessage = createAsyncThunk(
  "messaging/send",
  async (
    params: {
      conversationId: string
      body: string
      replyTo?: Message["reply_to"]
      attachments?: File[]
      clientToken: string
      onProgress?: (percent: number) => void
    } & Scope,
    { dispatch }
  ) => {
    dispatch(
      messageQueued({
        client_token: params.clientToken,
        conversation: params.conversationId,
        body: params.body,
        reply_to: params.replyTo ?? null,
        attachment_names: (params.attachments ?? []).map((file) => file.name),
        created: new Date().toISOString(),
        status: "sending"
      })
    )

    // Held outside the store so a retry can resend the bytes; the pending row
    // only carries filenames.
    rememberAttachments(params.clientToken, params.attachments ?? [])

    const response = await api.sendMessage(
      params.conversationId,
      {
        body: params.body,
        reply_to: params.replyTo?.id,
        client_token: params.clientToken,
        attachments: params.attachments
      },
      params.portal,
      params.onProgress
    )

    if (!response.ok || !response.data) {
      // The files stay remembered: the retry needs them.
      dispatch(messageFailed(params.clientToken))
      return
    }
    forgetAttachments(params.clientToken)
    // `messageArrived` drops the pending row keyed by this same token, so the
    // optimistic bubble is replaced rather than joined.
    dispatch(messageArrived(response.data))
  }
)

export const retrySend = createAsyncThunk(
  "messaging/retrySend",
  async (
    params: {
      clientToken: string
      conversationId: string
      body: string
      replyTo?: Message["reply_to"]
      hadAttachments?: boolean
      onProgress?: (percent: number) => void
    } & Scope,
    { dispatch }
  ) => {
    // A message that went out with attachments must not come back as text:
    // without the `File` objects there is nothing to resend, and posting the
    // text alone under the same token would look like it succeeded.
    const files = recallAttachments(params.clientToken)
    if (params.hadAttachments && !files?.length) {
      dispatch(messageFailed(params.clientToken))
      showNotification.error({
        message: i18n.t(
          "messages.errors.attachmentsLost",
          "The files are no longer available to resend. Please attach them again."
        ) as string
      })
      return
    }

    dispatch(messageRetrying(params.clientToken))
    // The same token goes back, so a send that actually landed before the
    // timeout returns the original message instead of writing a second one.
    const response = await api.sendMessage(
      params.conversationId,
      {
        body: params.body,
        reply_to: params.replyTo?.id,
        client_token: params.clientToken,
        attachments: files
      },
      params.portal,
      params.onProgress
    )
    if (!response.ok || !response.data) {
      dispatch(messageFailed(params.clientToken))
      return
    }
    forgetAttachments(params.clientToken)
    dispatch(messageArrived(response.data))
  }
)

export const discardFailedMessage = createAsyncThunk(
  "messaging/discardFailed",
  async (clientToken: string, { dispatch }) => {
    forgetAttachments(clientToken)
    dispatch(messageDiscarded(clientToken))
  }
)

export const editMessage = createAsyncThunk(
  "messaging/edit",
  async (params: { messageId: string; body: string } & Scope, { dispatch }) => {
    const response = await api.editMessage(
      params.messageId,
      params.body,
      params.portal
    )
    if (!response.ok || !response.data) {
      showNotification.error({
        message: i18n.t("messages.errors.editFailed") as string
      })
      return
    }
    dispatch(messageChanged(response.data))
  }
)

export const deleteMessage = createAsyncThunk(
  "messaging/delete",
  async (params: { messageId: string } & Scope, { dispatch }) => {
    const response = await api.deleteMessage(params.messageId, params.portal)
    if (!response.ok || !response.data) {
      showNotification.error({
        message: i18n.t("messages.errors.deleteFailed") as string
      })
      return
    }
    dispatch(messageChanged(response.data))
  }
)

export const markRead = createAsyncThunk(
  "messaging/markRead",
  async (
    params: {
      conversationId: string
      upTo: string
      upToMessage?: string
    } & Scope,
    { dispatch }
  ) => {
    const response = await api.markConversationRead(
      params.conversationId,
      params.upTo,
      params.portal,
      params.upToMessage
    )
    if (!response.ok || !response.data) return
    dispatch(
      setUnreadCount({
        conversationId: params.conversationId,
        count: response.data.unread_count
      })
    )
  }
)

export const updateNotificationLevel = createAsyncThunk(
  "messaging/updateNotificationLevel",
  async (
    params: {
      conversationId: string
      level: NotificationLevel
    } & Scope,
    { dispatch }
  ) => {
    const response = await api.setNotificationLevel(
      params.conversationId,
      params.level,
      params.portal
    )
    if (!response.ok) return
    dispatch(
      setNotificationLevelFor({
        conversationId: params.conversationId,
        level: params.level
      })
    )
  }
)

export const persistDraft = createAsyncThunk(
  "messaging/persistDraft",
  async (
    params: { conversationId: string; draft: string } & Scope,
    { dispatch }
  ) => {
    dispatch(
      setDraft({ conversationId: params.conversationId, draft: params.draft })
    )
    await api.saveDraft(params.conversationId, params.draft, params.portal)
  }
)

export const fetchUnreadTotal = createAsyncThunk(
  "messaging/fetchUnreadTotal",
  async (params: Scope | undefined, { dispatch, getState }) => {
    const activeBranchId = params?.portal
      ? null
      : selectActiveBranchId(getState() as RootState)
    const response = await api.getUnreadTotal(
      params?.portal,
      activeBranchId ?? undefined
    )
    if (!response.ok || !response.data) return
    dispatch(setUnreadTotal(response.data.unread_total))
  }
)

export const openDirectConversation = createAsyncThunk(
  "messaging/openDirect",
  async (params: { userId: string; recordId: string }, { dispatch }) => {
    const response = await api.openDirectConversation(
      params.userId,
      params.recordId
    )
    if (!response.ok || !response.data) {
      showNotification.error({
        message: i18n.t("messages.errors.directFailed") as string
      })
      return null
    }
    dispatch(upsertConversation(response.data))
    return response.data
  }
)
